import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { isAgentRoute } from './agentRoute';
import { readConsent, subscribeConsent, type CookieConsent } from '@/lib/cookieConsent';

/**
 * LE TAG LINKEDIN INSIGHTS DE L'AGENT — partenaire 9769418, et de lui seul.
 *
 * LA DEMANDE. LinkedIn Campaign Manager demande son snippet « posé sur la
 * page /agent » : c'est la page d'atterrissage des campagnes, et le tag y
 * mesure les conversions LinkedIn (donne aussi les listes de remarketing et
 * les statistiques démographiques des visiteurs).
 *
 * POURQUOI UN COMPOSANT — même raison que `GoogleAdsAgentTag`, posé juste
 * avant lui : le snippet officiel se colle dans le <head>, c'est-à-dire sur
 * ce site monodocument TOUTES les pages, prérendues comprises. La route
 * `/agent` est protégée (enfant d'OsShell sous `ProtectedRoute`), sans
 * version prérendue : le tag n'a de sens qu'au navigateur, au moment où la
 * route est montée. D'où ce composant, au-dessus des routes, qui lit
 * `useLocation` et n'injecte le script QUE sur `/agent` et ses enfants —
 * la frontière partagée vit dans `agentRoute.ts`.
 *
 * LE CONSENTEMENT — c'est ici la vraie différence avec le tag Google Ads.
 * Celui-là hérite du Consent Mode posé par `index.html` : chargé après, il
 * relit les défauts `denied` et n'écrit aucun cookie sans accord (pings
 * sans cookie). LinkedIn n'A PAS d'équivalent : `insight.min.js` ne lit
 * aucune file de consentement, et ses pixels émettent quoi qu'il arrive.
 * Le site promet en trois langues que « les cookies non essentiels ne sont
 * déposés qu'avec votre accord » — la seule façon honnête de poser ce tag
 * est donc d'attendre l'accord, sur le patron d'`IntercomProvider` et
 * `ContentsquareProvider` : ÉCOUTÉ plutôt que lu une fois, pour qu'accepter
 * depuis le bandeau suffise, sans rechargement. La catégorie qui l'ouvre est
 * `analytics` — comme Contentsquare : le bandeau n'a pas de catégorie
 * `marketing`, et en inventer une ici l'ajouterait de fait.
 *
 * LA NOSCRIPT DU SNIPPET OFFICIEL N'EST PAS REPOSÉE. Elle ne dit rien à un
 * visiteur : elle sert aux agents sans JavaScript, qui ne verraient de toute
 * façon jamais cette route — elle est montée par un composant React. La
 * copier serait décoratif.
 *
 * UNE FOIS PAR SESSION, ET PAS DE RETOUR EN ARRIÈRE. Un script ne se
 * décharge pas, et LinkedIn ne publie AUCUNE commande d'opt-out documentée
 * (rien qui ressemble au `_uxa.push(['optout'])` de Contentsquare) : retirer
 * son accord n'arrête pas un tag déjà parti, et ce fichier ne prétend pas
 * mieux. Ce que la barrière garantit : sans accord, rien n'est jamais
 * chargé — même pas le pixel.
 *
 * En production seulement — sur le patron d'`ENABLED` dans
 * `ContentsquareProvider` : `VITE_LINKEDIN_DEV=true` lève la barrière pour
 * vérifier le tag en local.
 */

/** Partenaire LinkedIn (Campaign Manager) du projet Aikka. Public par nature. */
const LINKEDIN_PARTNER_ID = '9769418';

/** Le loader Insights — l'URL exacte du snippet officiel LinkedIn. */
const INSIGHT_SRC = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';

/** Production, ou dev avec la barrière levée. */
export function linkedinEnabled(): boolean {
  return import.meta.env.PROD || import.meta.env.VITE_LINKEDIN_DEV === 'true';
}

interface LintrkWindow extends Window {
  /** La file d'appels `lintrk` — tenue par le snippet officiel. */
  lintrk?: (...args: unknown[]) => void;
  lintrkQ?: IArguments[];

  /** Les identifiants partenaire, lus par `insight.min.js`. */
  _linkedin_data_partner_ids?: string[];
}

/**
 * La file `lintrk` du snippet officiel — une coquille vide si le loader
 * n'est pas encore descendu, pour qu'un futur appel de conversion
 * (`lintrk('track', { conversion_id })`) attende dedans au lieu de tomber.
 */
function ensureLintrkQueue(): void {
  const w = window as LintrkWindow;
  if (typeof w.lintrk !== 'function') {
    w.lintrkQ = [];
    // eslint-disable-next-line func-style -- la forme « function » seule donne un vrai `arguments`.
    w.lintrk = function () {
      // eslint-disable-next-line prefer-rest-params -- `arguments` est la charge utile que le loader relit.
      w.lintrkQ?.push(arguments);
    };
  }
}

/**
 * Pose le tag — une seule fois par document, idempotent sur les allers et
 * retours de navigation comme sur les réponses successives du bandeau.
 *
 * L'ordre du snippet officiel est conservé : l'identifiant partenaire d'abord
 * (c'est lui que `insight.min.js` lit en descendant), puis la file `lintrk`,
 * puis le <script> asynchrone.
 */
function loadLinkedInTag(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const w = window as LintrkWindow;
  w._linkedin_data_partner_ids = w._linkedin_data_partner_ids || [];
  if (!w._linkedin_data_partner_ids.includes(LINKEDIN_PARTNER_ID)) {
    w._linkedin_data_partner_ids.push(LINKEDIN_PARTNER_ID);
  }
  ensureLintrkQueue();

  if (document.querySelector('script[data-linkedin-insight]')) return;

  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.src = INSIGHT_SRC;
  script.dataset.linkedinInsight = LINKEDIN_PARTNER_ID;
  document.head.appendChild(script);
}

/**
 * Le tag LinkedIn Insights de la page agent.
 *
 * À placer au-dessus des routes dans `App.tsx`, sous le routeur, à côté de
 * `GoogleAdsAgentTag`. Ne rend rien.
 */
export function LinkedInInsightTag() {
  const location = useLocation();
  const [consent, setConsent] = useState<CookieConsent | null>(() => readConsent());

  useEffect(() => subscribeConsent(setConsent), []);

  useEffect(() => {
    if (!linkedinEnabled()) return;
    if (!isAgentRoute(location.pathname)) return;
    // L'accord analytics ouvre le tag — voir l'en-tête : LinkedIn n'a pas de
    // Consent Mode, c'est la barrière qui porte la promesse de /privacy.
    if (consent?.analytics !== true) return;
    loadLinkedInTag();
  }, [location.pathname, consent]);

  return null;
}
